import { Box, Button, ButtonBase, createStyles, makeStyles } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import { getPichinchaPaymentLink } from '../../content/entities/pichincha/getPichinchaPaymentLink';
import useDictionary from '../../dictionaries/context/useDictionary';
import { BankType, DebtType, NegotiationOption, OutDebt } from '../../generated/graphql';
import eventsTracking from '../../utils/eventsTracking';
import { getEntityLabel } from '../../utils/financialEntitiesList';
import { getBetaPaymentLink } from '../../utils/getBetaPaymentLink';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { getPaymentLinkMovistarMoney } from '../../utils/getPaymentLinkMovistarMoney';
import { TranquiTheme } from '../App/theme';
import AnotherPaymentChannelsModal from './ConfirmationView/AnotherPaymentChannelsModal';

interface DebtDetNegConfirPayBtnProps {
  outDebt: OutDebt;
  prePayment?: number | undefined;
  className?: string;
  negOption: NegotiationOption;
}

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  payBtn: {
    fontWeight: 'bold',
  },
  pseBtn: {
    marginBottom: theme.spacing(2),
  },
  allDebtsBtn: {
    color: theme.palette.lavender.main,
    textDecoration: 'underline',
    textAlign: 'center',
    fontWeight: 400,
    marginTop: theme.spacing(2),
  },
}));

const PayLink: { [key in BankType]?: string } = {
  [BankType.BancoDavivienda]: 'https://www.davivienda.com/wps/portal/personas/nuevo/personas/aqui_puedo/pagar_facilmente/pse/!ut/p/z1/',
  [BankType.Bbva]: 'https://www.bbva.com.co/personas/tus-pagos.html',
  [BankType.Pichincha]: 'https://www.bancopichincha.com.co/web/personas/paga-tu-tarjeta-de-credito',
  [BankType.Rappipay]: 'https://rappicard.co/manual/pagar-la-tarjeta-no-es-un-problema-para-el-lifehacker',
  [BankType.RappiPrestamo]: 'https://rappicard.co/manual/pagar-la-tarjeta-no-es-un-problema-para-el-lifehacker',
  [BankType.BancoDeBogota]: 'https://www.bancodebogota.com/wps/portal/banco-de-bogota/bogota/atencion-al-cliente/canales-electronicos/canales/todos-canales',
  [BankType.NoBank]: '',
};

const DebtDetailNegConfirmationPayBtn: React.FC<DebtDetNegConfirPayBtnProps> = (props) => {
  const { outDebt, prePayment, className, negOption } = props;
  const { bank, type } = outDebt;
  const classes = useStyles();
  const [ openModal, setOpenModal ] = React.useState(false);
  let payLink: string | undefined = PayLink[bank];
  const {
    isDav,
    isPichincha,
    isTotalPayment,
    isRappiPay,
    isRappiPrestamo,
    isBeta,
    isBbva,
    isConfirmedByUser,
    isRejected,
    isBogota,
    isMovistarMoney,
    isJcap,
    isPaymentPromise,
    isRefinance,
    isCreditCard,
    isTranqui,
    isCatchUp,
    isApex,
    isApexRappi,
    isApexPopular,
    isApexOccidente,
    isPra,
    isClaro,
  } = getNegBooleans(outDebt, negOption);
  const { gT } = useDictionary();

  if (isApex) payLink = getPaymentLinkMovistarMoney(outDebt, gT, negOption);
  const isPichinchaPaymentPromise = isPichincha && isPaymentPromise;

  if (isPichinchaPaymentPromise) {
    payLink = getPichinchaPaymentLink({
      isPichincha,
      isPaymentPromise,
      isRefinance,
      isCreditCard,
      gT,
    });
  }

  if (isBeta) payLink = getBetaPaymentLink(outDebt, gT);

  const noPayLink = !payLink
    || (!prePayment && isDav)
    || (isPichincha && isTotalPayment && !(isConfirmedByUser || isRejected));
  if (noPayLink && !isJcap && !isPra && !isClaro) return null;

  const entityLabel = getEntityLabel(bank, (outDebt.brand as string) || undefined);
  const handleExternalClick = () => eventsTracking.page.external(payLink!);
  const pseLink = gT('pse_link');
  const handlePseClick = () => eventsTracking.page.external(pseLink);
  const handleModalToggle = () => setOpenModal((prev) => !prev);

  const isDaviplata = !!outDebt.extra?.daviplata;
  const isPayrollAdvance = type === DebtType.PayrollAdvance;
  const otherText = (() => {
    if (isBogota) return 'Guías de pago Banco de Bogotá';
    if (isMovistarMoney && isCatchUp) return '¿Cómo realizar el pago? Guías de pago';
    if (isDaviplata) return 'Medios de pago Daviplata';
    if (isJcap) return 'Medios de pago JCAP';
    if (isBeta) return 'Otros medios de pago.';
    if (isApex) return 'Otros medios de pago.';
    if (isPra) return 'Medios de pago PRA Group';
    if (isClaro) return 'Canales de pago Claro';
    return `Otros medios de pago ${entityLabel}`;
  })();

  const withOtherBtn = !isPichincha && !isRappiPay && !isRappiPrestamo && !isBbva
  && !(isApex && (isApexRappi || isApexPopular || isApexOccidente));

  const showPaymentButton = !isDaviplata
    && !isJcap
    && !isPra
    && !isClaro // Todo: no tenemos enlace, por eso se oculta
    && !(isApex && (isApexRappi || isApexPopular || isApexOccidente))
    && !(isBogota && isTotalPayment)
    && !(isBbva && isPayrollAdvance);
  return (
    <Box className={cn(classes.root, className)}>
      {(isBogota && isTranqui) && (
      <Button
        className={cn(classes.pseBtn)}
        variant="contained"
        color="primary"
        onClick={handlePseClick}
        href={pseLink}
        target="_blank"
        data-test="cy-debtDetailNegConfirmationPse-btn"
      >
        {gT('negotiation_confirmation_pse_button_text')}
      </Button>
      )}
      {showPaymentButton && (
        <Button
          variant="contained"
          color="inherit"
          onClick={handleExternalClick}
          href={payLink}
          target="_blank"
          data-test="cy-debtDetailNegConfirmationDavPay-btn"
          component="a"
        >
          {gT('negotiation_confirmation_payment_button_text', { bank: entityLabel })}
        </Button>
      )}
      {withOtherBtn && (
        <ButtonBase
          className={classes.allDebtsBtn}
          data-test="cy-otherPaymentChannels-btn"
          onClick={handleModalToggle}
        >
          {otherText}
        </ButtonBase>
      )}
      <AnotherPaymentChannelsModal
        openModal={openModal}
        onClose={handleModalToggle}
        isDav={isDav}
        isDaviplata={isDaviplata}
        isBeta={isBeta}
        isJcap={isJcap}
        isApex={isApex}
        isPra={isPra}
        isClaro={isClaro}
        isTranqui={isTranqui}
        isBogota={isBogota}
        negOption={negOption}
        outDebt={outDebt}
      />
    </Box>
  );
};

export default DebtDetailNegConfirmationPayBtn;
